import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@artstorefronts/ui';
import ImageAttachment from './ImageAttachment';
import MessageTimestamp from './MessageTimestamp';
import { resolveAttachmentDisplayUrl } from './utils/fileParts';
import { sameOtherProps } from './utils/memoProps';
import { resolveMessageTimestamp } from './utils/messageTimestamp';
import { useLibraryImages } from '~/hooks/useLibraryImages';

// v5 UIMessage: .parts only; derive display text from text parts
function getMessageDisplayText(message: any): string {
  if (message.parts?.length) {
    const text = message.parts
      .filter((p: any) => p.type === 'text')
      .map((p: any) => p.text ?? '')
      .join('\n\n');
    if (text.trim()) return text;
  }
  return typeof message.content === 'string' ? message.content : '';
}

// v5: combine file parts from message.parts AND message.experimental_attachments (messages can have text in parts AND attachments in experimental_attachments)
function getMessageAttachments(
  message: any
): Array<{ url: string; mediaType?: string; contentType?: string; name?: string }> {
  const fileParts = (message.parts || [])
    .filter((p: any) => p.type === 'file' && p.url)
    .map((p: any) => ({
      url: p.url,
      displayUrl: p.displayUrl,
      mediaType: p.mediaType,
      contentType: p.mediaType ?? p.contentType,
      name: p.filename ?? p.name,
      imageId: p.imageId != null ? String(p.imageId) : undefined,
    }));
  const expAttachments = (message.experimental_attachments || []).map((att: any) => ({
    ...att,
    contentType: att.contentType ?? att.mediaType,
    displayUrl: att.displayUrl ?? (att.url?.startsWith('data:') ? undefined : att.url),
    imageId: att.imageId != null ? String(att.imageId) : undefined,
  }));
  return [...fileParts, ...expAttachments];
}

function UserMessage({
  message,
  contentType,
  contentId,
  onSharePrompt,
  isFirstUserMessage,
  hasShareParams,
}: {
  message: any;
  contentType?: string;
  contentId?: string;
  onSharePrompt?: () => void;
  isFirstUserMessage?: boolean;
  hasShareParams?: boolean;
}) {
  const { t } = useTranslation('chat');
  const location = useLocation();

  // All hooks must be called before any conditional logic (v5: attachments from parts or experimental_attachments)
  const attachments = useMemo(() => {
    const list = getMessageAttachments(message);
    const shouldFilterMainImage = (attachment: any) => {
      if (contentType !== 'image' || !contentId) {
        return false;
      }

      const isArtRoute = location.pathname.match(/^\/art\/\d+/);
      if (!isArtRoute) {
        return false;
      }

      if (attachment.imageId != null && String(attachment.imageId) === String(contentId)) {
        return true;
      }

      const optimizedUrlPattern = new RegExp(`/optimized/${contentId}(?:[/?]|$)`);
      const candidateUrls = [attachment.displayUrl, attachment.url].filter(
        (url): url is string => typeof url === 'string' && !url.startsWith('data:')
      );

      return candidateUrls.some((url) => optimizedUrlPattern.test(url));
    };

    return list.filter((att: any) => !shouldFilterMainImage(att));
  }, [message.parts, message.experimental_attachments, contentType, contentId, location.pathname]);

  // Only an attachment with no stable URL of its own falls back to its library row.
  const lookupIds = useMemo(
    () => attachments.filter((att: any) => !resolveAttachmentDisplayUrl(att)).map((att: any) => att.imageId),
    [attachments]
  );
  const { find } = useLibraryImages(lookupIds);

  const withResolvedDisplayUrl = useCallback(
    (attachment: any) => {
      const image = find(attachment.imageId);
      return {
        ...attachment,
        url: resolveAttachmentDisplayUrl(attachment, image ? [image] : []),
      };
    },
    [find]
  );

  // Separate images from other files (v5: mediaType or contentType); hide attachments with no stable URL.
  const imageAttachments = useMemo(
    () =>
      attachments
        .filter((att: any) => (att.mediaType ?? att.contentType)?.startsWith('image/'))
        .map(withResolvedDisplayUrl)
        .filter((att: any) => Boolean(att.url)),
    [attachments, withResolvedDisplayUrl]
  );

  const fileAttachments = useMemo(
    () =>
      attachments
        .filter((att: any) => !(att.mediaType ?? att.contentType)?.startsWith('image/'))
        .map(withResolvedDisplayUrl)
        .filter((att: any) => Boolean(att.url)),
    [attachments, withResolvedDisplayUrl]
  );

  const displayText = getMessageDisplayText(message);

  // Guard clause - early return after all hooks
  if (message.role !== 'user') {
    return null;
  }

  const hasText = displayText.trim().length > 0;

  // Download function for attachments
  const downloadFile = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error(error);
      toast.error(t('message.failed-to-download-file'));
    }
  };

  const getGridClass = () => {
    // Always use single column for vertical stacking
    return 'grid-cols-1';
  };

  return (
    <div className="py-4 flex justify-end w-full">
      <div className="flex flex-col items-end gap-3 w-full">
        {/* Image attachments - grid layout */}
        {imageAttachments.length > 0 && (
          <div className={`grid gap-2 ${getGridClass()}`}>
            {imageAttachments.map((attachment: any, index: number) => (
              <ImageAttachment key={index} attachment={attachment} index={index} />
            ))}
          </div>
        )}

        {/* File attachments */}
        {fileAttachments.length > 0 && (
          <div className="flex flex-col gap-3">
            {fileAttachments.map((attachment: any, index: number) => {
              const fileExtension = attachment.name?.split('.').pop()?.toUpperCase() || 'FILE';
              return (
                <button
                  key={index}
                  onClick={() => downloadFile(attachment.url, attachment.name || `file-${index + 1}`)}
                  className="flex items-center bg-white border border-gray-100 rounded-2xl py-1.5 px-3 min-w-[200px] shadow-xs h-16 hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-pink-600 flex items-center justify-center flex-shrink-0 mr-3">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {attachment.name || t('message.untitled-file')}
                    </div>
                    <div className="text-xs text-gray-500 uppercase">{fileExtension}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Text message (v5: from parts) */}
        {hasText && (
          <div className="bg-gray-100 text-gray-800 font-light text-base leading-7 rounded-3xl py-2 px-6 max-w-[80%]">
            {displayText}
          </div>
        )}

        {/* Share prompt button - only on first user message with shareParams */}
        {onSharePrompt && hasText && isFirstUserMessage && hasShareParams && (
          <div className="flex justify-end mt-2">
            <Button
              className="px-3 py-1.5 h-auto text-xs rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors font-medium"
              variant="ghost"
              onClick={onSharePrompt}
              title={t('message.share-this-prompt')}
            >
              <Sparkles className="w-4 h-4 mr-1.5" />
              {t('message.share-this-prompt')}
            </Button>
          </div>
        )}

        <MessageTimestamp message={message} />
      </div>
    </div>
  );
}

export default React.memo(UserMessage, (prevProps, nextProps) => {
  // Flags and callbacks first: the first-message share button depends on them, not on `message`.
  if (!sameOtherProps(prevProps, nextProps)) {
    return false;
  }

  // Same message object → every check below is equal by construction. Skipping them keeps a
  // re-render of a long transcript O(rows) instead of re-serialising every attachment list.
  if (prevProps.message === nextProps.message) {
    return true;
  }

  return (
    prevProps.message.id === nextProps.message.id &&
    resolveMessageTimestamp(prevProps.message)?.getTime() === resolveMessageTimestamp(nextProps.message)?.getTime() &&
    getMessageDisplayText(prevProps.message) === getMessageDisplayText(nextProps.message) &&
    JSON.stringify(getMessageAttachments(prevProps.message)) ===
      JSON.stringify(getMessageAttachments(nextProps.message))
  );
});
